import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/** @type {Promise<number | null> | undefined} */
let cartSyncPromise;

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';

    // Keep the localized screen reader text and enclosing control in sync.
    const accessibleCount = this.refs.cartBubbleText.querySelector('.visually-hidden');
    if (accessibleCount) {
      accessibleCount.textContent = accessibleCount.textContent.replace(/:\s*\d+\s*$/, `: ${value}`);
    }

    const cartAction = this.closest('button[aria-label], a[aria-label]');
    if (cartAction) {
      cartAction.setAttribute('aria-label', cartAction.getAttribute('aria-label').replace(/:\s*\d+\s*$/, `: ${value}`));
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.ensureCartBubbleIsCorrect);
    window.addEventListener('focus', this.ensureCartBubbleIsCorrect);

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.ensureCartBubbleIsCorrect);
    window.removeEventListener('focus', this.ensureCartBubbleIsCorrect);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} animate - Whether to animate the cart bubble.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', this.currentCartCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;

    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Revalidates stale cart sections against the same cart used by the counter.
   * @returns {Promise<number | null>}
   */
  async #getSynchronizedCartCount() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) return null;

    const cart = await response.json();
    const count = Number(cart.item_count || 0);
    const sectionIds = new Set();

    for (const items of document.querySelectorAll('cart-items-component')) {
      const renderedCount = Number(items.querySelector('[ref="cartItemCount"]')?.textContent || 0);
      const sectionId = items.getAttribute('data-section-id');
      if (sectionId && renderedCount !== count) sectionIds.add(sectionId);
    }

    await Promise.all([...sectionIds].map((sectionId) => sectionRenderer.renderSection(sectionId, { cache: false })));
    return count;
  }

  /**
   * Shares in-flight revalidation across connection, pageshow and focus events.
   */
  ensureCartBubbleIsCorrect = async () => {
    try {
      cartSyncPromise ??= this.#getSynchronizedCartCount().finally(() => {
        cartSyncPromise = undefined;
      });
      const count = await cartSyncPromise;
      if (count === null) return;

      await this.renderCartBubble(count, false, false);

      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(count),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}